/**
 * MAP STAGE (owner B2): the React host for the map canvas.
 *
 *   <MapStage onReady={(ctrl) => …} onError={…} className="…" />
 *
 * - Waits for preload.essential (the data rule), then creates the controller once per mount and calls
 *   onReady(ctrl) after ctrl.ready (style, anchors, terrain, LightLayer, overlays and markers attached).
 *   StrictMode's double mount is safe: a disposed mount never creates, and cleanup destroys.
 * - Pixel ratio: `?pr=<1–2>` wins; kiosk renders the map at 1 (spec §A.1); otherwise the device ratio,
 *   capped at 2 (fill-rate on 3× phones).
 * - `?light=gl|fallback|auto` picks the LightLayer mode.
 * - The div fills its parent (position absolute, inset 0). MapLibre resizes itself with its own
 *   ResizeObserver on this element.
 * - Accessibility (§H.2): the container is a region with roledescription "map"; the canvas is focusable,
 *   labelled "Use arrow keys to pan, plus and minus to zoom." and handles those keys.
 */
import { useEffect, useRef, type JSX } from "react";
import type { MapController } from "@/contracts/map";
import type { MapStageProps } from "@/contracts/ui";
import { cx } from "@/design";
import { preload } from "@/features/loader/preload";
import { getApp } from "@/store/store";
import { createController, type ControllerOptions } from "./controller";
import "./stage.css";

export const MAP_REGION_LABEL = "Map of places around Ládera City";

/** Reads ?pr, ?kiosk and ?light (and the store's kiosk flag) into controller options. */
export function stageOptions(search: string = typeof location !== "undefined" ? location.search : ""): Omit<ControllerOptions, "reducedMotion"> {
  const q = new URLSearchParams(search);
  const kiosk = getApp().kiosk || q.get("kiosk") === "1";
  const prRaw = Number(q.get("pr"));
  const dpr = typeof window !== "undefined" && window.devicePixelRatio ? window.devicePixelRatio : 1;
  const pixelRatio = Number.isFinite(prRaw) && prRaw > 0 ? Math.min(2, Math.max(1, prRaw)) : kiosk ? 1 : Math.min(2, dpr);
  const lm = q.get("light");
  const lightMode = lm === "gl" || lm === "fallback" ? lm : "auto";
  return { pixelRatio, lightMode, ...(kiosk ? { layout: "kiosk" as const } : {}) };
}

export function MapStage({ onReady, onError, className }: MapStageProps): JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const cbs = useRef({ onReady, onError });
  useEffect(() => {
    cbs.current = { onReady, onError };
  }, [onReady, onError]);

  useEffect(() => {
    const el = host.current;
    if (!el) return undefined;
    let disposed = false;
    let ctrl: MapController | null = null;
    const fail = (err: unknown) => {
      if (disposed) return;
      const h = cbs.current.onError;
      if (h) h(err);
      else console.error("[MapStage]", err);
    };
    preload.essential
      .then(() => {
        if (disposed) return undefined;
        ctrl = createController(el, { ...stageOptions(), reducedMotion: () => getApp().reducedMotion });
        const c = ctrl;
        return c.ready.then(() => {
          if (!disposed) cbs.current.onReady(c);
        });
      })
      .catch(fail);
    return () => {
      disposed = true;
      ctrl?.destroy();
      ctrl = null;
    };
  }, []);

  return <div ref={host} className={cx("ld-loc-map-root", className)} role="region" aria-roledescription="map" aria-label={MAP_REGION_LABEL} />;
}
